"use client";

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { cn } from "@/lib/utils";
import {
    BlockSplitMerge,
    CaptionBlock,
    LowConfidenceNavigation,
    TimestampDecoration,
    WordBucketAutoClear,
    WordMark,
    findLowConfidencePositions,
} from "./lib/extensions";
import {
    buildCaptionsDoc,
    serializeDocJsonToSrt,
    type TranscriptionBlock,
} from "./lib/utils";
import { scrollPosIntoSurface } from "./lib/scroll";
import FindReplacePanel, {
    type FindReplacePanelHandle,
} from "./FindReplacePanel";
import WordPopover from "./WordPopover";

import "./captionsEditor.css";

export type CaptionsEditorHandle = {
    getSrt: () => string;
    getTranscription: () => TranscriptionBlock[] | null;
    hasTranscriptionChanges: () => boolean;
    isDirty: () => boolean;
    markClean: () => void;
};

type Props = {
    srt: string;
    transcription: TranscriptionBlock[] | null;
    rtl: boolean;
    onDirtyChange?: (dirty: boolean) => void;
    onSaveHotkey?: () => void;
    setHelpOpen: (open: boolean) => void;
};

const CaptionsEditor = forwardRef<CaptionsEditorHandle, Props>(
    function CaptionsEditor(
        {
            srt,
            transcription,
            rtl,
            onDirtyChange,
            onSaveHotkey,
            setHelpOpen,
        },
        ref
    ) {
        const containerRef = useRef<HTMLDivElement>(null);
        const surfaceRef = useRef<HTMLDivElement>(null);
        const findReplaceRef = useRef<FindReplacePanelHandle>(null);
        const dirtyRef = useRef(false);
        const confidenceOverridesRef = useRef<Set<string>>(new Set());

        const initial = useMemo(
            () => buildCaptionsDoc({ srt, transcription }),
            // Only recompute when captions change (parent keys by captionsId).
            // eslint-disable-next-line react-hooks/exhaustive-deps
            []
        );

        const editor = useEditor(
            {
                immediatelyRender: false,
                extensions: [
                    StarterKit.configure({
                        paragraph: false,
                        heading: false,
                        blockquote: false,
                        bulletList: false,
                        orderedList: false,
                        listItem: false,
                        codeBlock: false,
                        code: false,
                        bold: false,
                        italic: false,
                        strike: false,
                        horizontalRule: false,
                    }),
                    CaptionBlock,
                    WordMark,
                    TimestampDecoration,
                    LowConfidenceNavigation,
                    BlockSplitMerge,
                    WordBucketAutoClear.configure({
                        onWordEdited: (blockIdx, wordIdx) => {
                            confidenceOverridesRef.current.add(
                                `${blockIdx}:${wordIdx}`
                            );
                        },
                    }),
                ],
                content: initial.doc,
                editorProps: {
                    attributes: {
                        class: "captions-editor-content focus:outline-none",
                    },
                    handleScrollToSelection: (view) =>
                        scrollPosIntoSurface(view, view.state.selection.head),
                },
                onUpdate: () => {
                    if (!dirtyRef.current) {
                        dirtyRef.current = true;
                        onDirtyChange?.(true);
                    }
                },
            },
            []
        );

        useImperativeHandle(
            ref,
            () => ({
                getSrt: () => {
                    if (!editor) return srt;
                    return serializeDocJsonToSrt(editor.getJSON());
                },
                getTranscription: () => {
                    const overrides = confidenceOverridesRef.current;
                    if (!transcription || overrides.size === 0)
                        return transcription;

                    const updatedTexts = new Map<string, string>();
                    if (editor) {
                        const wordType = editor.schema.marks.word;
                        editor.state.doc.descendants((node) => {
                            if (!node.isText) return;
                            const mark = node.marks.find(
                                (m) => m.type === wordType
                            );
                            if (!mark) return;
                            const key = `${mark.attrs.blockIdx}:${mark.attrs.wordIdx}`;
                            if (!overrides.has(key)) return;
                            updatedTexts.set(
                                key,
                                (updatedTexts.get(key) ?? "") + (node.text ?? "")
                            );
                        });
                    }

                    return transcription.map((block, bIdx) => ({
                        ...block,
                        words: block.words.map((w, wIdx) => {
                            const key = `${bIdx}:${wIdx}`;
                            if (!overrides.has(key)) return w;
                            const newText = updatedTexts.get(key);
                            return {
                                ...w,
                                confidence: 1,
                                ...(newText ? { word: newText } : {}),
                            };
                        }),
                    }));
                },
                hasTranscriptionChanges: () =>
                    confidenceOverridesRef.current.size > 0,
                isDirty: () =>
                    dirtyRef.current ||
                    confidenceOverridesRef.current.size > 0,
                markClean: () => {
                    dirtyRef.current = false;
                    confidenceOverridesRef.current = new Set();
                    onDirtyChange?.(false);
                },
            }),
            [editor, srt, transcription, onDirtyChange]
        );

        // Editor keyboard shortcuts (find/help/save).
        // Attach to document so the shortcut works whether or not focus is
        // currently inside the captions editor — e.g. after a Replace, focus
        // can land outside the container which otherwise swallows Ctrl+F.
        useEffect(() => {
            const onKey = (e: KeyboardEvent) => {
                const mod = e.metaKey || e.ctrlKey;
                if (!mod) return;
                const container = containerRef.current;
                if (!container || !document.body.contains(container)) return;
                if (e.key === "f" || e.key === "F") {
                    e.preventDefault();
                    if (editor) {
                        const { from, to, empty } = editor.state.selection;
                        if (!empty) {
                            const text = editor.state.doc
                                .textBetween(from, to, " ")
                                .trim();
                            if (text) {
                                findReplaceRef.current?.setFindText(text);
                                return;
                            }
                        }
                    }
                    findReplaceRef.current?.focusFind();
                } else if (e.key === "/") {
                    e.preventDefault();
                    setHelpOpen(true);
                } else if (e.key === "s" || e.key === "S") {
                    if (onSaveHotkey) {
                        e.preventDefault();
                        onSaveHotkey();
                    }
                }
            };
            document.addEventListener("keydown", onKey, true);
            return () => document.removeEventListener("keydown", onKey, true);
        }, [onSaveHotkey, setHelpOpen, editor]);

        return (
            <div
                ref={containerRef}
                className="captions-editor-root relative flex flex-col gap-2"
                dir={rtl ? "rtl" : "ltr"}
            >
                <FindReplacePanel
                    ref={findReplaceRef}
                    editor={editor}
                    onOpenHelp={() => setHelpOpen(true)}
                />

                <div
                    ref={surfaceRef}
                    className={cn(
                        "captions-editor-surface relative rounded-md border border-border bg-background",
                        "max-h-[420px] overflow-y-auto"
                    )}
                >
                    <EditorContent editor={editor} />
                </div>
                <WordPopover
                    editor={editor}
                    rootRef={containerRef}
                    surfaceRef={surfaceRef}
                    onMarkCorrect={(blockIdx, wordIdx) => {
                        confidenceOverridesRef.current.add(
                            `${blockIdx}:${wordIdx}`
                        );
                        if (!dirtyRef.current) {
                            dirtyRef.current = true;
                            onDirtyChange?.(true);
                        }
                    }}
                />
            </div>
        );
    }
);

export default CaptionsEditor;
